import configs from '../config';
import { fetchWithTenant } from '../utils/apiClient';

const API_BASE_URL = `${configs.BACKEND_URL}/api/saved-jobs`;

/**
 * Save a job
 * @param {string} jobId - The job ID to save
 * @param {string} notes - Optional notes for the saved job
 */
export const saveJob = async (jobId, notes = '') => {
  const response = await fetchWithTenant(API_BASE_URL, {
    method: 'POST',
    body: JSON.stringify({ jobId, notes })
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to save job');
  }

  return response.json();
};

/**
 * Unsave a job
 * @param {string} jobId - The job ID to unsave
 */
export const unsaveJob = async (jobId) => {
  const response = await fetchWithTenant(`${API_BASE_URL}/${jobId}`, {
    method: 'DELETE'
  });

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to unsave job');
  }

  return response.json();
};

/**
 * Get all saved jobs for the current user
 * @param {Object} options - Query options (page, limit)
 */
export const getSavedJobs = async (options = {}) => {
  const params = new URLSearchParams();
  if (options.page) params.append('page', options.page);
  if (options.limit) params.append('limit', options.limit);

  const url = params.toString() ? `${API_BASE_URL}?${params}` : API_BASE_URL;
  const response = await fetchWithTenant(url);

  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || 'Failed to fetch saved jobs');
  }

  return response.json();
};

/**
 * Check if a specific job is saved
 * @param {string} jobId - The job ID to check
 */
export const isJobSaved = async (jobId) => {
  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/check/${jobId}`);
    if (!response.ok) return false;
    const data = await response.json();
    return data.isSaved || false;
  } catch {
    return false;
  }
};

/**
 * Check saved status for multiple jobs
 * @param {string[]} jobIds - Array of job IDs to check
 */
export const checkSavedJobs = async (jobIds) => {
  if (!jobIds || jobIds.length === 0) return {};

  try {
    const response = await fetchWithTenant(`${API_BASE_URL}/check-bulk`, {
      method: 'POST',
      body: JSON.stringify({ jobIds })
    });

    if (!response.ok) return {};
    const data = await response.json();
    return data.savedJobs || {};
  } catch {
    return {};
  }
};

export default {
  saveJob,
  unsaveJob,
  getSavedJobs,
  isJobSaved,
  checkSavedJobs
};
